/**
 * The ONLY place this app talks to the delivery-proof line.
 *
 * There is no TypeScript here to catch a response-shape change, so a single
 * access layer is the whole of the containment: when the backend's declared
 * resource moves, one file moves with it rather than every view.
 *
 * Head office both REVIEWS the proofs and reads the numbers they produce. The
 * review used to live in `hal-branch-admin-frontend`, scoped to the reviewer's
 * own branch — which made the party the evidence was about the party judging it.
 * Moved here 2026-08-13; the branch keeps only its sign-out screen.
 *
 * Contract: `hal-logistics-backend` →
 * `openspec/changes/add-customer-delivery-proof/contracts/api.md`.
 */
import client from 'axios';

/**
 * The report's own axios instance — same reasoning as `Claim/ClaimApi.js`.
 *
 * These endpoints sit behind `Localization`, which reads the **`Locale` header**
 * and accepts only `en`, `lo`, `zh`. This app stores its language as
 * `Lang=LA|CN` and sends it as a `lang` QUERY PARAM, which that middleware
 * ignores. Adding the header to the shared instance would change every request
 * in the application, so this line carries its own.
 *
 * ⚠️ `la` is NOT accepted on the wire even though it is the app's own code for
 * Lao — the backend maps `lo` onto its internal `la` AFTER the check.
 */
// TH maps to 'en' until the API's Localization middleware lists 'th' (unknown locales 403).
const LOCALE_BY_APP_LANG = { LA: 'lo', CN: 'zh', EN: 'en', TH: 'th' };

const proofHttp = client.create({ baseURL: process.env.VUE_APP_BASE_API_URL_HTTP });

proofHttp.interceptors.request.use((config) => {
    const token = window.localStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers.Locale = LOCALE_BY_APP_LANG[window.localStorage.getItem('Lang')] || 'lo';
    return config;
});

const REPORTS = 'admin/delivery-proofs/reports';
const REVIEW = 'delivery-proofs';

/**
 * Which senders proof of delivery is open for at all.
 *
 * The feature is CLOSED by default: with this list empty, no customer anywhere
 * can confirm a collection, and the queue and report above stay empty forever.
 *
 * Contract: `hal-logistics-backend` →
 * `openspec/changes/gate-delivery-proof-by-sender/contracts/api.md`.
 */
const SENDERS = 'admin/delivery-proofs/senders';

/**
 * The customer search behind the enable form: COD-approved customers only, by
 * the START of the phone number or the name, or an exact customer code.
 *
 * Built for this screen (2026-10-16). It used to be the generic `list-customer`,
 * which matches every column with a leading `%` across all ~9.8M customers and
 * counts them — about a minute per search on prod. It returns `customers.id`,
 * which is what the preview and enable calls take.
 */
const CUSTOMER_SEARCH = `${SENDERS}/candidates`;

export default {
    data: () => ({
        /** The last refusal, kept on screen rather than in a toast that has gone. */
        lastReportError: null,
    }),

    methods: {
        /**
         * Per-branch totals for a period.
         *
         * Every figure comes back computed. Nothing here re-derives a total, a
         * median or a closure count — an aggregate computed over one page would
         * be the median OF THAT PAGE, and would disagree with the same number
         * elsewhere without ever looking wrong.
         */
        deliveryProofSummary(params = {}) {
            return proofHttp.get(`${REPORTS}/summary`, { params });
        },

        /** The bills behind any figure, so a disputed number names its bills. */
        deliveryProofRows(params = {}) {
            return proofHttp.get(`${REPORTS}/rows`, { params });
        },

        /**
         * The same selection as `deliveryProofRows`, as a spreadsheet.
         *
         * Built by the SERVER and unpaginated: a file assembled from the page on
         * screen is not the set anybody meant to send, and this one leaves the
         * building. `responseType: blob` because the body is a binary file, not
         * the JSON envelope every other call here returns.
         */
        deliveryProofRowsExport(params = {}) {
            return proofHttp.get(`${REPORTS}/rows/export`, { params, responseType: 'blob' });
        },

        // ── review ──────────────────────────────────────────────────────────

        /**
         * The queue, across every branch.
         *
         * `branch_id` narrows the VIEW; it is never what decides access — the
         * role gate on the endpoint is.
         */
        deliveryProofQueue(params = {}) {
            return proofHttp.get(REVIEW, { params });
        },

        deliveryProofDetail(id) {
            return proofHttp.get(`${REVIEW}/${id}`);
        },

        /** Head office accepts the customer's evidence. Does NOT sign the bill. */
        deliveryProofApprove(id) {
            return proofHttp.post(`${REVIEW}/${id}/approve`);
        },

        /**
         * Rejects the evidence, on the record. The proof stays in the queue and
         * stays counted; the customer is notified so they can retake.
         */
        deliveryProofReject(id, body) {
            return proofHttp.post(`${REVIEW}/${id}/reject`, body);
        },

        /** Reason codes, so this app holds no copy of the list. */
        deliveryProofConfig() {
            return proofHttp.get(`${REVIEW}/config`);
        },

        // ── sign on behalf ──────────────────────────────────────────────────

        /**
         * May this bill be closed in the branch's name — by THIS caller?
         *
         * The answer covers both halves: `outcome` is about the bill (its proof
         * must be approved and it must not already be signed) and
         * `caller_may_sign` is about the permission. `may_sign` is the AND, and
         * it is what a button binds to.
         *
         * Read-only. Asked before the action is offered, so a reviewer is never
         * shown something that would be refused — this app cannot answer the
         * permission half itself, because `$can()` reads roles out of local
         * storage and the authority here is a per-user permission.
         */
        deliveryProofSignOnBehalfEligibility(billNumber) {
            return proofHttp.get(`${REVIEW}/sign-on-behalf/${encodeURIComponent(billNumber)}`);
        },

        /**
         * Close the bill in the destination branch's name.
         *
         * Multipart: `bill_number`, `signature` (the image the ACTING USER
         * attaches — the customer's proof photograph is the evidence that
         * permits this, never the bill's signature), and an optional `note`.
         *
         * Sends no branch identifier and never a list. The endpoint prohibits
         * both: the branch credited comes from the BILL, and a list-driven sign
         * is how the older bulk path came to close bills nobody had looked at.
         *
         * This is the only call on this mixin that moves money — freight, COD,
         * the branch wallet and customer points all settle, on the destination
         * branch.
         */
        deliveryProofSignOnBehalf(formData) {
            return proofHttp.post(`${REVIEW}/sign-on-behalf`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },

        // ── senders ─────────────────────────────────────────────────────────

        /**
         * Every period, open and withdrawn.
         *
         * ONE ROW IS ONE PERIOD, not one sender: a sender opened, withdrawn and
         * opened again has two rows, and a bill is covered by whichever period
         * was open when the bill was raised. Closed periods are never removed —
         * the record of who authorised the review load is the point of keeping
         * them — so this screen shows them rather than filtering them away.
         */
        deliveryProofSenders(params = {}) {
            return proofHttp.get(SENDERS, { params });
        },

        /**
         * One period's bill counts (`covered`, `signed`, `awaiting`), on request.
         *
         * The list no longer carries them: counting every row of every page took
         * the page to a minute for one large shop. Cached 10 min on the server;
         * `state` is `ok`, `busy` (being counted — ask again) or `timed_out`.
         */
        deliveryProofSenderCounts(id) {
            return proofHttp.get(`${SENDERS}/${id}/counts`);
        },

        /**
         * What enabling this customer WOULD cover. Writes nothing.
         *
         * The only place three things are visible: every `customers` row sharing
         * the number (enabling covers all of them, because coverage is by phone
         * number and not by row), the recent despatch volume — roughly the
         * monthly review load about to arrive — and whether the sender is
         * already open.
         */
        deliveryProofSenderPreview(customerId) {
            return proofHttp.post(`${SENDERS}/preview`, { customer_id: customerId });
        },

        /**
         * Opens a period.
         *
         * Takes a CUSTOMER ID and never a phone number: the backend matches
         * `customers.tel` exactly, stored numbers carry separators and invisible
         * characters, and a typed number that misses covers nothing while
         * reporting no error at all. The endpoint refuses a `tel` in the body
         * for that reason — so this screen must never offer the field.
         */
        deliveryProofSenderEnable(body) {
            return proofHttp.post(SENDERS, body);
        },

        /**
         * Closes a period. The row is stamped, never deleted, and nothing
         * already recorded is touched: proofs stay in the queue, stay in the
         * report, stay readable by the customer.
         *
         * The response carries `bills_still_covered` — bills raised while the
         * period was open KEEP their coverage, so submissions keep arriving
         * afterwards.
         */
        deliveryProofSenderDisable(id, body = {}) {
            return proofHttp.delete(`${SENDERS}/${id}`, { data: body });
        },

        /**
         * COD-approved customers matching a phone / name prefix or a customer code
         * (at most 20, no paging).
         *
         * ⚠️ The field the enable and preview calls need is `id` — the row id.
         * `customer_id` on the same object is the display code (`HAL-…`) and is
         * NOT interchangeable with it.
         */
        searchCustomersForSender(query) {
            return proofHttp.get(CUSTOMER_SEARCH, { params: { q: query } });
        },

        handleReportError(error) {
            this.lastReportError =
                error?.response?.data?.message || this.$t('deliveryProof.report_load_failed');
        },
    },
};
